/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import SearchFilterBar from '@/components/UI/SearchFilterBar';
import SelectDropDown from '@/components/UI/SelectDropDown';
import ActionList from '@/components/UI/SelectDropDown/Menus/ActionList';
import TableFilterMenu from '@/components/UI/SelectDropDown/Menus/TableFilterMenu';
import UseTableShowMenu from '@/components/UI/SelectDropDown/Menus/UseTableShowMenu';
import Spinner from '@/components/UI/Spinner';
import { useTableContext } from '@/components/UI/Table/TableContext';
import { _isEmpty, _isNotEmpty } from '@/littledash';
import { type FC, type ReactNode } from 'react';
import { type Row } from '@tanstack/react-table';

// Types for props - matching existing component interfaces
interface BulkAction {
  name: string;
  action: () => void;
  disabled?: boolean;
  destructive?: boolean;
}

interface FilterOption {
  name: string;
  value: string;
}

interface SearchQueryByOption {
  label: string;
  value: string;
}

interface ActionBarProps {
  bulkActions?: BulkAction[];
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  filterOptions?: FilterOption[];
  filters: Record<string, unknown>[];
  setFilters: (filters: Record<string, unknown>[]) => void;
  filterMatch: boolean;
  setFilterMatch: (match: boolean) => void;
  searchPlaceholderText: string;
  updating: boolean;
  selectAllActive?: boolean;
  handleSelectAllClick?: () => void;
  searchQueryBy?: string;
  setSearchQueryBy?: (queryBy: string) => void;
  searchQueryByOptions?: SearchQueryByOption[];
  tableName?: string;
  showMetricChangeFrom?: boolean;
  hideSearch?: boolean;
  hideFilter?: boolean;
  hasSelectorColumn?: boolean;
}

const ActionBar: FC<ActionBarProps> = ({
  bulkActions,
  searchQuery,
  setSearchQuery,
  filterOptions,
  filters,
  setFilters,
  filterMatch,
  setFilterMatch,
  searchPlaceholderText,
  updating,
  selectAllActive = false,
  handleSelectAllClick,
  searchQueryBy,
  setSearchQueryBy,
  searchQueryByOptions,
  tableName,
  showMetricChangeFrom = false,
  hideSearch = false,
  hideFilter = false,
  hasSelectorColumn = false,
}) => {
  const tableContext = useTableContext();

  // Extract values from TanStack Table v8 context with proper typing
  const selectedFlatRows =
    ((tableContext as Record<string, unknown>).selectedFlatRows as Row<Record<string, unknown>>[]) || [];
  const isAllRowsSelected = ((tableContext as Record<string, unknown>).isAllRowsSelected as boolean) || false;
  const totalItemCount = (tableContext as Record<string, unknown>).totalItemCount as number | null;
  const pageSizes = ((tableContext as Record<string, unknown>).pageSizes as number[]) || [10, 25, 50, 100];
  const rows = ((tableContext as Record<string, unknown>).rows as Row<Record<string, unknown>>[]) || [];

  // Extract pageSize from state
  const state = ((tableContext as Record<string, unknown>).state as Record<string, unknown>) || {};
  const pageSize = (state.pageSize as number) || 10;

  const searchFilterBarProps = {
    searchPlaceholderText,
    searchBy: searchQueryBy,
    searchByOptions: searchQueryByOptions,
    setSearchBy: setSearchQueryBy,
    handleSearchInput: setSearchQuery,
    searchQuery,
    hideSearch,
    hideFilter,
    ShowMenu: () => (
      <UseTableShowMenu
        closeMenu={() => {}} // Required prop
        showMetricChangeFrom={showMetricChangeFrom}
        tableName={tableName || 'default'}
        hasSelectorColumn={hasSelectorColumn}
      />
    ),
    FilterMenu: undefined as (() => ReactNode) | undefined,
  };

  if (_isNotEmpty(filterOptions)) {
    searchFilterBarProps.FilterMenu = () => (
      <TableFilterMenu
        filterOptions={filterOptions}
        filters={filters}
        filterMatch={filterMatch}
        onUpdateFilters={(newFilters: Record<string, unknown> | undefined, newFilterMatch: boolean) => {
          setFilters(Array.isArray(newFilters) ? newFilters : newFilters ? [newFilters] : []);
          setFilterMatch(newFilterMatch);
        }}
      />
    );
  }

  const renderSelectAllLinkText = (): ReactNode => {
    if (selectAllActive) {
      if (updating) {
        return (
          <div className="flex items-center">
            <Spinner size="small" color="blue" />{' '}
            <span className="ml2 lh-title">Fetching all table rows, this may take a moment...</span>
          </div>
        );
      }
      return 'Clear selection';
    }
    return `Select all ${selectedFlatRows.length}+ items in this table`;
  };

  const canSelectAll = (pageSizes.includes(1000) && pageSize === rows.length) || selectAllActive;

  const hideSearchAndFilter = hideSearch && hideFilter;

  return hideSearchAndFilter && _isEmpty(bulkActions) ? (
    <></>
  ) : (
    <div className="w-100">
      {!hideSearchAndFilter && <SearchFilterBar {...searchFilterBarProps} />}
      {_isNotEmpty(bulkActions) && (
        <div className={`w-100 flex items-center ${!hideSearchAndFilter ? 'mt3' : ''}`}>
          {_isNotEmpty(selectedFlatRows) && (
            <div
              className="mid-gray bg-light-gray ph3 ba b--moon-gray f6 br-0 br--left br1"
              style={{
                whiteSpace: 'nowrap',
                lineHeight: '2.4rem',
              }}
            >
              {selectedFlatRows.length} selected
            </div>
          )}
          <SelectDropDown
            title="Bulk actions"
            className={`plain f6 ${_isNotEmpty(selectedFlatRows) ? 'br--right' : ''}`}
            alignMenu="right"
            disabled={_isEmpty(selectedFlatRows)}
            testId="animals__bulk-action--button"
          >
            <ActionList actions={bulkActions || []} testPrefix="animals__" />
          </SelectDropDown>
          {canSelectAll &&
            isAllRowsSelected &&
            (totalItemCount ?? 1000) > selectedFlatRows.length &&
            handleSelectAllClick && (
              <a
                className={`blue link ml3 f6 ${updating ? 'pointer-events-none' : ''}`}
                onClick={handleSelectAllClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelectAllClick();
                  }
                }}
              >
                {renderSelectAllLinkText()}
              </a>
            )}
        </div>
      )}
    </div>
  );
};

export default ActionBar;
